import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCoins, fetchStats, fetchQualityStats, fetchConvictionStats } from "../lib/api";
import { useChain } from "../context/ChainContext";
import { ChainToggle } from "../components/ChainToggle";
import { MarketStats } from "../components/MarketStats";
import { Filters } from "../components/Filters";
import { CoinTable } from "../components/CoinTable";
import { TelegramConnect } from "../components/TelegramConnect";
import { CommandCenter } from "../components/CommandCenter";
import { WorldClock } from "../components/WorldClock";
import { EarlyGambles } from "../components/EarlyGambles";
import { FreshGraduates } from "../components/FreshGraduates";
import { ApeWatermark } from "../components/ApeWatermark";
import { TrendingOrganic } from "../components/TrendingOrganic";
import { GlobalAgeControl, ageMaxHours } from "../components/AgeFilter";
import { TrashBin } from "../components/TrashBin";
import { useGlobalAge } from "../context/AgeFilterContext";
import { TodaysRunner } from "../components/TodaysRunner";
import { ProvenBuilders } from "../components/ProvenBuilders";
import { TechTokens } from "../components/TechTokens";
import { TechRecap } from "../components/TechRecap";
import { RunnerRecap } from "../components/RunnerRecap";
import { SignInReminder } from "../components/SignInReminder";
import { useTrustedCreators } from "../lib/useTrustedCreators";
import { useRugLedger } from "../lib/useRugLedger";
import { useTrash } from "../lib/useTrash";
import { eliteCheck } from "../lib/elite";
import { isWhaleMagnet } from "../lib/whale";
import { isSurvivor } from "../lib/survivor";
import { useAlphaChime } from "../lib/alphaSound";
import { WarningCircleIcon, SpinnerGapIcon, AddressBookIcon, DiamondIcon, TrashIcon, SkullIcon, LightningIcon, FireIcon, LeafIcon, PushPinIcon } from "@phosphor-icons/react";

export default function Dashboard() {
  const FKEY = "alphaapeing_radar_filters";
  const saved = (() => { try { return JSON.parse(localStorage.getItem(FKEY) || "{}"); } catch { return {}; } })();

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [minScore, setMinScore] = useState(saved.minScore ?? 0);
  const { age: globalAge } = useGlobalAge();
  const maxAge = ageMaxHours(globalAge);
  const [surging, setSurging] = useState(saved.surging ?? false);
  const [devOnly, setDevOnly] = useState(saved.devOnly ?? false);
  const [eliteOnly, setEliteOnly] = useState(saved.eliteOnly ?? false);
  const [hideVamps, setHideVamps] = useState(saved.hideVamps ?? false);
  const [fastOnly, setFastOnly] = useState(saved.fastOnly ?? false);
  const [highVol, setHighVol] = useState(saved.highVol ?? false);
  const [whaleOnly, setWhaleOnly] = useState(saved.whaleOnly ?? false);
  const [survivorOnly, setSurvivorOnly] = useState(saved.survivorOnly ?? false);
  const [qualityOnly, setQualityOnly] = useState(saved.qualityOnly ?? false);
  const [organicOnly, setOrganicOnly] = useState(saved.organicOnly ?? false);
  const [convictionOnly, setConvictionOnly] = useState(saved.convictionOnly ?? false);
  const [lpSafeOnly, setLpSafeOnly] = useState(saved.lpSafeOnly ?? false);
  const [showLowQuality, setShowLowQuality] = useState(saved.showLowQuality ?? false);
  const CC_PIN_KEY = "alphaapeing_hidecopycats_pinned";
  const [copycatPinned, setCopycatPinned] = useState(() => localStorage.getItem(CC_PIN_KEY) === "1");
  const [hideCopycats, setHideCopycats] = useState((localStorage.getItem(CC_PIN_KEY) === "1") || (saved.hideCopycats ?? false));
  const [showTrashed, setShowTrashed] = useState(false);
  const [sort, setSort] = useState(saved.sort ?? "score");
  const [order, setOrder] = useState(saved.order ?? "desc");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Paste a contract address → jump straight to that token's detail page.
  useEffect(() => {
    const v = search.trim();
    if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v)) navigate(`/coin/${v}`);
  }, [search, navigate]);

  useEffect(() => {
    localStorage.setItem(FKEY, JSON.stringify({ minScore, surging, devOnly, eliteOnly, hideVamps, fastOnly, highVol, whaleOnly, survivorOnly, qualityOnly, organicOnly, convictionOnly, lpSafeOnly, showLowQuality, hideCopycats, sort, order }));
  }, [minScore, surging, devOnly, eliteOnly, hideVamps, fastOnly, highVol, whaleOnly, survivorOnly, qualityOnly, organicOnly, convictionOnly, lpSafeOnly, showLowQuality, hideCopycats, sort, order]);

  const { chain } = useChain();
  const isSol = chain === "solana";
  const statsQ = useQuery({ queryKey: ["stats", chain], queryFn: () => fetchStats(chain) });
  const qualityStatsQ = useQuery({ queryKey: ["qualityStats"], queryFn: fetchQualityStats, refetchInterval: 120000 });
  const qToday = qualityStatsQ.data?.today ?? null;
  const convictionStatsQ = useQuery({ queryKey: ["convictionStats"], queryFn: fetchConvictionStats, refetchInterval: 120000 });
  const cToday = convictionStatsQ.data?.today ?? null;
  const coinsQ = useQuery({
    queryKey: ["coins", chain, sort, order, minScore, maxAge, debounced],
    queryFn: () =>
      fetchCoins({
        chain,
        sort,
        order,
        min_score: minScore,
        ...(maxAge ? { max_age_hours: maxAge } : {}),
        ...(debounced ? { search: debounced } : {}),
      }),
    refetchInterval: 60000,
    placeholderData: (prev) => prev,
  });

  const onSort = (key) => {
    if (sort === key) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else { setSort(key); setOrder("desc"); }
  };

  const coins = coinsQ.data?.coins || [];
  const isSearching = !!debounced.trim();
  const surgingStates = new Set(["run", "heating", "waking"]);
  let visibleCoins = (!isSearching && surging) ? coins.filter((c) => surgingStates.has(c.momentum?.state)) : coins;

  const { isAlpha } = useTrustedCreators();
  const { isBlockedCreator } = useRugLedger();
  const { isTrashed, trashedSet } = useTrash();
  const trackedCount = coins.filter((c) => isAlpha(c.creator) || isBlockedCreator(c.creator)).length;
  const eliteCount = coins.filter((c) => eliteCheck(c).isElite).length;
  const vampCount = coins.filter((c) => c.vamp?.status === "vamp").length;
  const isFastMover = (c) => c.velocity?.direction === "up" || c.velocity?.direction === "down";
  const fastCount = coins.filter(isFastMover).length;
  const HIGH_VOL = 1_000_000;
  const isHighVol = (c) => ((c.volume?.h24) || 0) >= HIGH_VOL;
  const highVolCount = coins.filter(isHighVol).length;
  const whaleCount = coins.filter(isWhaleMagnet).length;
  const survivorCount = coins.filter(isSurvivor).length;
  const isQuality = (c) => isSurvivor(c) && isWhaleMagnet(c);
  const qualityCount = coins.filter(isQuality).length;
  const organicCount = coins.filter((c) => c.discovery === "organic").length;
  const HIGH_CONVICTION = 75;
  const isHighConviction = (c) => (c.conviction ?? 0) >= HIGH_CONVICTION;
  const convictionCount = coins.filter(isHighConviction).length;
  const isCopycat = (c) => c.nameCollision && !c.nameLeader;
  const copycatCount = coins.filter(isCopycat).length;
  const lpSafeCount = coins.filter((c) => c.lpLocked).length;
  const lowQualityCount = coins.filter((c) => c.lowQuality).length;
  const trashedCount = coins.filter((c) => isTrashed(c.address)).length;
  // When the user is searching, show the raw search results — the radar filter
  // chips (Conviction, Organic, LP-safe, etc.) must NOT strip out what they searched for.
  if (!isSearching) {
    if (devOnly) visibleCoins = visibleCoins.filter((c) => isAlpha(c.creator) || isBlockedCreator(c.creator));
    if (eliteOnly) visibleCoins = visibleCoins.filter((c) => eliteCheck(c).isElite);
    if (hideVamps) visibleCoins = visibleCoins.filter((c) => c.vamp?.status !== "vamp");
    if (fastOnly) visibleCoins = visibleCoins.filter(isFastMover);
    if (highVol) visibleCoins = visibleCoins.filter(isHighVol);
    if (whaleOnly) visibleCoins = visibleCoins.filter(isWhaleMagnet);
    if (survivorOnly) visibleCoins = visibleCoins.filter(isSurvivor);
    if (qualityOnly) visibleCoins = visibleCoins.filter(isQuality);
    if (organicOnly) visibleCoins = visibleCoins.filter((c) => c.discovery === "organic");
    if (convictionOnly) visibleCoins = visibleCoins.filter(isHighConviction);
    if (lpSafeOnly) visibleCoins = visibleCoins.filter((c) => c.lpLocked);
    if (hideCopycats) visibleCoins = visibleCoins.filter((c) => !isCopycat(c));
    if (!showLowQuality) visibleCoins = visibleCoins.filter((c) => !c.lowQuality);
    if (!showTrashed) visibleCoins = visibleCoins.filter((c) => !isTrashed(c.address));
  }
  useAlphaChime(coins.filter((c) => isAlpha(c.creator)).map((c) => c.address));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      {/* Brand hero */}
      <div className="flex items-center gap-4 sm:gap-6 pt-2 pb-1">
        <img
          src="/logo.png"
          alt="AlphaApeing"
          className="h-20 sm:h-28 w-auto object-contain shrink-0 drop-shadow-[0_0_30px_rgba(0,229,153,0.35)]"
        />
        <div className="min-w-0">
          <h1 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter leading-[0.9]">
            ALPHA<span className="text-neon">APEING</span>
          </h1>
          <p className="font-mono text-[11px] sm:text-xs text-neon tracking-[0.25em] mt-1">
            SOLANA MEME-COIN INTELLIGENCE
          </p>
        </div>
      </div>

      {/* Market switch — controls the whole Radar below */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <ChainToggle />
        <Link
          to="/trending"
          data-testid="trending-shortcut"
          className="flex items-center gap-2 px-3.5 py-2 text-xs sm:text-sm font-bold border transition-colors animate-pulse-hot shrink-0"
          style={{ color: "#FF7A1A", borderColor: "#FF7A1A66", background: "#FF7A1A0D" }}
        >
          <FireIcon size={16} weight="fill" className="animate-pulse" /> Trending Meme Themes
        </Link>
      </div>

      {/* Radar section header */}
      <div data-testid="radar-section" className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-t border-white/10 pt-5">
        <div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter leading-none">
            THE <span className="text-neon">RADAR</span>
          </h2>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Live {isSol ? "Solana" : "Robinhood Chain"} meme coins ranked by <span className="text-neon font-semibold">Degen Score</span> — a
            transparent 0-100 read on volume, liquidity safety, buy pressure, survival & credibility.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <span className="w-2 h-2 rounded-full bg-neon animate-pulse" />
          LIVE · {isSol ? "Solana" : "Robinhood Chain"} · via DexScreener
        </div>
      </div>

      <GlobalAgeControl />

      <TrashBin />

      {!isSol && (
        <div data-testid="hood-notice" className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.06] px-4 py-2.5 text-xs sm:text-sm text-muted-foreground">
          <span className="text-[#CCFF00] font-semibold">🪶 Robinhood Chain (HOOD) is live</span> — Radar, Early Gambles & Degen Score are running now.
          Smart Money, Command Center, Clusters & alerts are Solana-only for now and coming to HOOD next.
        </div>
      )}

      <SignInReminder />

      {isSol && <CommandCenter />}

      <WorldClock />

      {isSol && <TelegramConnect />}

      <MarketStats stats={statsQ.data} loading={statsQ.isLoading} />

      {isSol && <TodaysRunner />}

      {isSol && <ProvenBuilders />}

      {isSol && <TechTokens />}

      {isSol && <TechRecap />}

      {isSol && <RunnerRecap />}

      <Filters
        search={search} setSearch={setSearch}
        minScore={minScore} setMinScore={setMinScore}
        surging={surging} setSurging={setSurging}
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="radar-quality-toggle"
          onClick={() => setQualityOnly((v) => !v)}
          title="The killer shortlist — launches that LASTED (Survivor) AND that whales are actively buying (Whale Magnet). As close to a real-runner list as we can get for free."
          className={`flex items-center gap-2 px-4 py-2 text-sm font-bold border transition-colors ${
            qualityOnly ? "bg-[#F0C000]/20 border-[#F0C000] text-[#F0C000]" : "border-[#F0C000]/40 text-[#F0C000]/90 hover:bg-[#F0C000]/10"
          }`}
        >
          🏆 Quality
          {qualityCount > 0 && <span className="font-mono text-xs opacity-70">({qualityCount})</span>}
          {qToday > 0 && <span className="font-mono text-[10px] px-1 py-0.5 bg-[#F0C000]/20 rounded-sm" title={`${qToday} fresh launches have crossed into Quality today`}>{qToday} today</span>}
        </button>
        <button
          data-testid="radar-devonly-toggle"
          onClick={() => setDevOnly((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            devOnly ? "bg-[#C9A227]/15 border-[#C9A227]/40 text-[#F0C000]" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <AddressBookIcon size={16} weight={devOnly ? "fill" : "regular"} /> Tracked devs only
          {trackedCount > 0 && <span className="font-mono text-xs opacity-70">({trackedCount})</span>}
        </button>
        <button
          data-testid="radar-eliteonly-toggle"
          onClick={() => setEliteOnly((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            eliteOnly ? "bg-[#22D3EE]/15 border-[#22D3EE]/40 text-[#22D3EE]" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <DiamondIcon size={16} weight={eliteOnly ? "fill" : "regular"} /> Elite only
          {eliteCount > 0 && <span className="font-mono text-xs opacity-70">({eliteCount})</span>}
        </button>
        <button
          data-testid="radar-hidevamps-toggle"
          onClick={() => setHideVamps((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            hideVamps ? "bg-danger/15 border-danger/40 text-danger" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <SkullIcon size={16} weight={hideVamps ? "fill" : "regular"} /> Hide vamps
          {vampCount > 0 && <span className="font-mono text-xs opacity-70">({vampCount})</span>}
        </button>
        <button
          data-testid="radar-fastonly-toggle"
          onClick={() => setFastOnly((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            fastOnly ? "bg-[#FF7A00]/15 border-[#FF7A00]/50 text-[#FF7A00]" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <LightningIcon size={16} weight={fastOnly ? "fill" : "regular"} /> Fast movers only
          {fastCount > 0 && <span className="font-mono text-xs opacity-70">({fastCount})</span>}
        </button>
        <button
          data-testid="radar-highvol-toggle"
          onClick={() => setHighVol((v) => !v)}
          title="Show only coins doing $1M+ in 24h volume — the real movers"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            highVol ? "bg-[#F0C000]/15 border-[#F0C000]/50 text-[#F0C000]" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <FireIcon size={16} weight={highVol ? "fill" : "regular"} /> High volume ($1M+)
          {highVolCount > 0 && <span className="font-mono text-xs opacity-70">({highVolCount})</span>}
        </button>
        <button
          data-testid="radar-whale-toggle"
          onClick={() => setWhaleOnly((v) => !v)}
          title="Show only coins where whale-sized money is flowing in — big average trade size + buy pressure (free DexScreener signal, no wallet tracking needed)"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            whaleOnly ? "bg-[#22D3EE]/15 border-[#22D3EE]/50 text-[#22D3EE]" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          🐋 Whale Magnet
          {whaleCount > 0 && <span className="font-mono text-xs opacity-70">({whaleCount})</span>}
        </button>
        <button
          data-testid="radar-survivor-toggle"
          onClick={() => setSurvivorOnly((v) => !v)}
          title="Show only launches that actually LASTED — ≥10 min old, real liquidity ($15k+), still trading, and not dumped 90%. Filters out the 5-minute 40k rugs. Free signal, no Helius."
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            survivorOnly ? "bg-neon/15 border-neon/50 text-neon" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          🛡️ Survivors
          {survivorCount > 0 && <span className="font-mono text-xs opacity-70">({survivorCount})</span>}
        </button>
        <button
          data-testid="radar-organic-toggle"
          onClick={() => setOrganicOnly((v) => !v)}
          title="Hide paid-boosted tokens — show only coins that surfaced organically (real demand)"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            organicOnly ? "bg-neon/15 border-neon/50 text-neon" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <LeafIcon size={16} weight={organicOnly ? "fill" : "regular"} /> Organic only
          {organicCount > 0 && <span className="font-mono text-xs opacity-70">({organicCount})</span>}
        </button>
        <button
          data-testid="radar-conviction-toggle"
          onClick={() => setConvictionOnly((v) => !v)}
          title="👀 ANSEM-tier only — coins with a Conviction score ≥ 75. Credibility-blind: pure volume + survival + buy pressure. Catches real runners even when the token info looks weak."
          className={`flex items-center gap-2 px-4 py-2 text-sm font-bold border transition-colors ${
            convictionOnly ? "bg-[#F0C000]/20 border-[#F0C000] text-[#F0C000]" : "border-[#F0C000]/40 text-[#F0C000]/90 hover:bg-[#F0C000]/10"
          }`}
        >
          👀 Conviction
          {convictionCount > 0 && <span className="font-mono text-xs opacity-70">({convictionCount})</span>}
          {cToday > 0 && <span className="font-mono text-[10px] px-1 py-0.5 bg-[#F0C000]/20 rounded-sm" title={`${cToday} coins have crossed ANSEM-tier conviction today`}>{cToday} today</span>}
        </button>
        <button
          data-testid="radar-lpsafe-toggle"
          onClick={() => setLpSafeOnly((v) => !v)}
          title="Show only 🔒 LP-locked/burned coins — the dev can't pull liquidity. One less rug vector. (Coverage grows as tokens get scanned.)"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-bold border transition-colors ${
            lpSafeOnly ? "bg-[#4ADE80]/20 border-[#4ADE80] text-[#4ADE80]" : "border-[#4ADE80]/40 text-[#4ADE80]/90 hover:bg-[#4ADE80]/10"
          }`}
        >
          🔒 LP safe
          {lpSafeCount > 0 && <span className="font-mono text-xs opacity-70">({lpSafeCount})</span>}
        </button>
        <button
          data-testid="radar-hidecopycats-toggle"
          onClick={() => setHideCopycats((v) => !v)}
          title="Hide copycat same-ticker tokens — when several tokens share a name, keep only the likely-real leader (ranked #1 by liquidity, volume, age & score)"
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
            hideCopycats ? "bg-danger/15 border-danger/40 text-danger" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          🔀 Hide copycats
          {copycatCount > 0 && <span className="font-mono text-xs opacity-70">({copycatCount})</span>}
        </button>
        <button
          data-testid="radar-hidecopycats-pin"
          onClick={() => {
            const next = !copycatPinned;
            setCopycatPinned(next);
            localStorage.setItem(CC_PIN_KEY, next ? "1" : "0");
            if (next) setHideCopycats(true);
          }}
          title={copycatPinned ? "Copycats stay hidden on every visit (pinned). Tap to unpin." : "Pin: keep copycats hidden by default on every visit"}
          className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-semibold border transition-colors ${
            copycatPinned ? "bg-danger/15 border-danger/40 text-danger" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <PushPinIcon size={14} weight={copycatPinned ? "fill" : "regular"} />
          {copycatPinned ? "Default on" : "Pin default"}
        </button>
        <Link
          to="/organic-runners"
          data-testid="radar-organic-runners-link"
          title="Dedicated hub for survivor-then-run organic runners"
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-neon/40 text-neon hover:bg-neon/10 transition-colors"
        >
          🌱 Organic Runners →
        </Link>
        {lowQualityCount > 0 && (
          <button
            data-testid="radar-showlowquality-toggle"
            onClick={() => setShowLowQuality((v) => !v)}
            title="Junk gate — dead pools, thin liquidity (< $8k), can't-exit ratios & dumping low-scorers are hidden by default so the Radar stays clean. A real runner (TRUMP/ANSEM) always has deep liquidity, so gems are never hidden. Tap to reveal the hidden ones (dimmed + badged)."
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
              showLowQuality ? "bg-danger/15 border-danger/40 text-danger" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
            }`}
          >
            <WarningCircleIcon size={16} weight={showLowQuality ? "fill" : "regular"} /> {showLowQuality ? "Hide low-quality" : "Show low-quality"}
            <span className="font-mono text-xs opacity-70">({lowQualityCount})</span>
          </button>
        )}
        {trashedCount > 0 && (
          <button
            data-testid="radar-showtrashed-toggle"
            onClick={() => setShowTrashed((v) => !v)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
              showTrashed ? "bg-danger/15 border-danger/40 text-danger" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
            }`}
          >
            <TrashIcon size={16} weight={showTrashed ? "fill" : "regular"} /> {showTrashed ? "Hide trashed" : "Show trashed"}
            <span className="font-mono text-xs opacity-70">({trashedCount})</span>
          </button>
        )}
      </div>

      {coinsQ.isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Scanning the chain…</span>
        </div>
      ) : coinsQ.isError ? (
        <div className="flex flex-col items-center justify-center py-24 text-danger gap-3">
          <WarningCircleIcon size={32} />
          <span className="font-mono text-sm">Failed to load market data. Retrying shortly…</span>
        </div>
      ) : visibleCoins.length === 0 ? (
        <div className="relative overflow-hidden border border-white/10 bg-card py-24 text-center text-muted-foreground font-mono text-sm">
          <ApeWatermark opacity={0.05} size="220px" />
          <span className="relative z-10">{isSearching ? `No tokens found for "${debounced.trim()}". Try a different name / ticker, or paste the full contract address.` : surging ? "No coins are surging right now. Tap Surging again to see all." : "No coins match your filters."}</span>
        </div>
      ) : (
        <>
          {qualityOnly && qualityStatsQ.data?.recent?.length > 0 && (
            <div className="border border-[#F0C000]/25 bg-[#F0C000]/[0.04] px-4 py-3" data-testid="quality-history">
              <div className="text-[11px] uppercase tracking-wider text-[#F0C000] font-semibold mb-1.5">
                🏆 {qualityStatsQ.data.last24h} crossed into Quality in the last 24h
              </div>
              <div className="flex flex-wrap gap-1.5">
                {qualityStatsQ.data.recent.map((r) => (
                  <Link key={r.mint} to={`/coin/${r.mint}`} data-testid={`quality-hist-${r.mint}`}
                    className="text-[11px] font-mono px-2 py-0.5 border bg-[#F0C000]/10 border-[#F0C000]/30 text-[#F0C000] hover:bg-[#F0C000]/20 transition-colors">
                    ${r.symbol || r.mint.slice(0, 4)}{r.peakMultiple > 1.2 ? ` ${r.peakMultiple}×` : ""}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {convictionOnly && convictionStatsQ.data?.recent?.length > 0 && (
            <div className="border border-[#F0C000]/25 bg-[#F0C000]/[0.04] px-4 py-3" data-testid="conviction-history">
              <div className="text-[11px] uppercase tracking-wider text-[#F0C000] font-semibold mb-1.5">
                👀 {convictionStatsQ.data.last24h} crossed ANSEM-tier conviction in the last 24h
              </div>
              <div className="flex flex-wrap gap-1.5">
                {convictionStatsQ.data.recent.map((r) => (
                  <Link key={r.mint} to={`/coin/${r.mint}`} data-testid={`conviction-hist-${r.mint}`}
                    title={`Conviction ${r.conviction}/100 when first spotted`}
                    className="text-[11px] font-mono px-2 py-0.5 border bg-[#F0C000]/10 border-[#F0C000]/30 text-[#F0C000] hover:bg-[#F0C000]/20 transition-colors">
                    ${r.symbol || r.mint.slice(0, 4)} {r.conviction}{r.peakMultiple > 1.2 ? ` · ${r.peakMultiple}×` : ""}
                  </Link>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs font-mono text-muted-foreground">
            {visibleCoins.length} coins{surging ? " surging 🚀" : ""}
          </div>
          <CoinTable coins={visibleCoins} sort={sort} order={order} onSort={onSort} />
        </>
      )}

      {isSol && <FreshGraduates />}
      <EarlyGambles />
      {isSol && <TrendingOrganic />}
    </div>
  );
}
